import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { TrainingVideo, TrainingVideosByCategory, UUID } from '@cobliteam/common-types';

import { type UserTrainingMedia, UserTrainingMediaActionType } from '@cobliteam/api-lib/src/__generated__/graphql';
import GraphQLTrainingVideoRequester from '@cobliteam/api-lib/src/dashboard/trainingVideos/GraphQLTrainingVideoRequester';

export const QUERY_KEY = 'TRAINING_VIDEOS';

const trainingVideoGraphQlRequester = new GraphQLTrainingVideoRequester();

const useTrainingVideo = () => {
  const queryClient = useQueryClient();

  const getTrainingVideos = ({ ...options }: Partial<UseQueryOptions<TrainingVideo[]>>) => {
    const queryKey = [QUERY_KEY];

    return useQuery<TrainingVideo[]>({
      ...options,
      queryKey,
      queryFn: () => trainingVideoGraphQlRequester.getTrainingVideos(),
    });
  };

  const getAllTrainingVideosByCategory = ({ ...options }: Partial<UseQueryOptions<TrainingVideosByCategory[]>>) => {
    const queryKey = [`${QUERY_KEY}_BY_CATEGORY`];

    return useQuery<TrainingVideosByCategory[]>({
      ...options,
      queryKey,
      queryFn: () => trainingVideoGraphQlRequester.getAllTrainingVideosByCategory(),
    });
  };

  const getUserTrainingMedia = ({ ...options }: Partial<UseQueryOptions<UserTrainingMedia[]>>) => {
    const queryKey = [`${QUERY_KEY}_USER_TRAINING_MEDIA`];

    return useQuery<UserTrainingMedia[]>({
      ...options,
      queryKey,
      queryFn: () => trainingVideoGraphQlRequester.getUserTrainingMedia(),
    });
  };

  const startUserTrainingMedia = () => {
    return useMutation<UserTrainingMedia, unknown, UUID>({
      mutationFn: async (trainingMediaId: UUID) => await trainingVideoGraphQlRequester.updateUserTrainingMedia(trainingMediaId, UserTrainingMediaActionType.Start),
      onSuccess: (data: UserTrainingMedia) => {
        const previousUserTrainingMedia: UserTrainingMedia[] | undefined = queryClient.getQueryData([`${QUERY_KEY}_USER_TRAINING_MEDIA`]);

        queryClient.setQueryData([`${QUERY_KEY}_USER_TRAINING_MEDIA`], (old: UserTrainingMedia[] | undefined) => {
          if (!old) return old;

          const updatedUserTrainingMedia = old.map((userMedia) => {
            if (userMedia.trainingMediaId === data.trainingMediaId) {
              return data;
            }
            return userMedia;
          });

          return updatedUserTrainingMedia;
        });
      },
    });
  };

  const finishUserTrainingMedia = () => {
    return useMutation<UserTrainingMedia, unknown, UUID>({
      mutationFn: async (trainingMediaId: UUID) => await trainingVideoGraphQlRequester.updateUserTrainingMedia(trainingMediaId, UserTrainingMediaActionType.Finish),
      onSuccess: (data: UserTrainingMedia) => {
        const previousUserTrainingMedia: UserTrainingMedia[] | undefined = queryClient.getQueryData([`${QUERY_KEY}_USER_TRAINING_MEDIA`]);

        queryClient.setQueryData([`${QUERY_KEY}_USER_TRAINING_MEDIA`], (old: UserTrainingMedia[] | undefined) => {
          if (!old) return old;

          const updatedUserTrainingMedia = old.map((userMedia) => {
            if (userMedia.trainingMediaId === data.trainingMediaId) {
              return data;
            }
            return userMedia;
          });

          return updatedUserTrainingMedia;
        });
      },
    });
  };

  return {
    getTrainingVideos,
    getAllTrainingVideosByCategory,
    getUserTrainingMedia,
    startUserTrainingMedia,
    finishUserTrainingMedia,
  };
};

export default useTrainingVideo;
